import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { withCookies } from 'react-cookie';
import { useAtom } from 'jotai';
import { ThemeProvider } from 'react-jss';
import { ReactNotifications } from 'react-notifications-component';
import { useFullscreen } from 'react-use';
import { TourProvider } from '@reactour/tour';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ToastContainer } from 'react-toastify';
import ThemeContext from '../contexts/themeContext';
import Wrapper from '../layout/Wrapper/Wrapper';
import Portal from '../layout/Portal/Portal';
import useDarkMode from '../hooks/useDarkMode';
import COLORS from '../common/data/enumColors';
import { getOS } from '../helpers/helpers';
import steps, { styles } from '../steps';
import AsideRoutes from '../layout/Aside/AsideRoutes';
import { ToastCloseButton } from '../components/bootstrap/Toasts';
import { tokenAuth } from '../_services/user';
import { userAtom } from '../_atoms/user';
import { companyAtom } from '../_atoms/company';
import { getCompanyById } from '../_services/company';
import "dayjs/locale/tr"
import Modal, { ModalBody, ModalHeader, ModalTitle } from '../components/bootstrap/Modal';
import Alert, { AlertHeading } from '../components/bootstrap/Alert';

dayjs.locale("tr")

const App = ({ cookies }) => {
	const [token, setToken] = useState("")
	const [user, setUser] = useAtom(userAtom)
	const [company, setCompany] = useAtom(companyAtom)
	const [validModal, setValidModal] = useState(false)

	getOS();

	dayjs.extend(localizedFormat);
	dayjs.extend(relativeTime);

	useEffect(() => {
		const localToken = cookies.cookies.token || ""
		setToken(localToken)
	}, [cookies])

	useEffect(() => {
		if (user && user.company) {
			const data = { id: user.company }
			getCompanyById(user.token, data)
				.then(res => {
					if (res.data && res.data.code === 200) {
						setCompany(res.data.data)
						if (!res.data.valid) {
							setValidModal(true)
						}
					}
				})
		}
	}, [user, setCompany])

	useEffect(() => {
		if (token !== '' && token.length > 10) {
			tokenAuth(token)
				.then(res => {
					if (res.data && res.data.code === 200) {
						setUser({ ...res.data.data, loggedIn: true, token })
					} else {
						console.log("403")
					}
				})
				.catch(err => {
					console.log(err)
				})
		}
	}, [token, setUser])

	/**
	 * Dark Mode
	 */
	const { themeStatus, darkModeStatus } = useDarkMode();
	const theme = {
		theme: themeStatus,
		primary: COLORS.PRIMARY.code,
		secondary: COLORS.SECONDARY.code,
		success: COLORS.SUCCESS.code,
		info: COLORS.INFO.code,
		warning: COLORS.WARNING.code,
		danger: COLORS.DANGER.code,
		dark: COLORS.DARK.code,
		light: COLORS.LIGHT.code,
	};

	useEffect(() => {
		if (darkModeStatus) {
			document.documentElement.setAttribute('theme', 'dark');
			document.documentElement.setAttribute('data-bs-theme', 'dark');
		}
		return () => {
			document.documentElement.removeAttribute('theme');
			document.documentElement.removeAttribute('data-bs-theme');
		};
	}, [darkModeStatus]);

	/**
	 * Full Screen
	 */
	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const ref = useRef(null);
	useFullscreen(ref, fullScreenStatus, {
		onClose: () => setFullScreenStatus(false),
	});

	/**
	 * Modern Design
	 */
	useLayoutEffect(() => {
		if (process.env.REACT_APP_MODERN_DESGIN === 'true') {
			document.body.classList.add('modern-design');
		} else {
			document.body.classList.remove('modern-design');
		}
	});

	return (
		<ThemeProvider theme={theme}>
			<TourProvider steps={steps} styles={styles} showNavigation={false} showBadge={false}>
				<div
					ref={ref}
					className='app'
					style={{
						backgroundColor: fullScreenStatus ? 'var(--bs-body-bg)' : undefined,
						zIndex: fullScreenStatus ? 1 : undefined,
						overflow: fullScreenStatus ? 'scroll' : undefined,
					}}>
					<AsideRoutes />
					<Wrapper />
				</div>
				<Portal id='portal-notification'>
					<ReactNotifications />
				</Portal>
				<ToastContainer closeButton={ToastCloseButton} toastClassName='toast show' />
			</TourProvider>
			<Modal
				setIsOpen={() => null}
				isOpen={validModal}
				size='lg'
				fullScreen
				isScrollable
				data-tour='mail-app-modal'>
				<ModalHeader className='px-4' setIsOpen={() => null}>
					<ModalTitle id='project-edit'>Hesap Bilgilendirme</ModalTitle>
				</ModalHeader>
				<ModalBody style={{display : "flex", justifyContent : "center", alignItems : "center"}}>
					<Alert
						icon='Info'
						isLight
						color='primary'
						borderWidth={0}
						className='shadow-3d-primary'
					>
						<AlertHeading tag='h2' className='h4'>
							Bilgilendirme!
						</AlertHeading>
						<span>{company.name} isimli firma adına kayıtlı hesabın üyeliği {dayjs(company.validUntil).format("DD MMMM YYYY")} tarihi itibarı ile sona ermiştir. Üyeliğinizi uzatmak için lütfen bayinizle ya da OXY Erp genel merkezi ile iletişime geçiniz.</span>
					</Alert>
				</ModalBody>
			</Modal>

		</ThemeProvider>
	);
};

App.defaultProps = {
	cookies: PropTypes.shape({
		cookies: PropTypes.shape({
			token: PropTypes.string
		})
	}),

};

App.propTypes = {
	cookies: PropTypes.shape({
		cookies: PropTypes.shape({
			token: PropTypes.string
		})
	}),
};

export default withCookies(App);
